import { jsPDF } from "jspdf";

const formatDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const resolveAccount = (accounts, accountId) =>
  (accounts || []).find((a) => String(a?.id) === String(accountId)) || null;

const resolveAssignedUser = (companyUsers, assignedToId) =>
  (companyUsers || []).find((u) => String(u?.id) === String(assignedToId)) || null;

export const buildSampleOrderExportModel = ({
  request,
  form,
  accounts = [],
  inventory = [],
  companyUsers = [],
}) => {
  if (!request) return null;

  const accountId = form?.accountId ?? request.accountId ?? null;
  const account = resolveAccount(accounts, accountId);

  const assignedToId = form?.assignedToId ?? request.assignedToId ?? null;
  const assignedUser = resolveAssignedUser(companyUsers, assignedToId);

  const createdAt = formatDateValue(request.createdAt);
  const requestedDate = form?.requestedDate ?? request.requestedDate ?? "";
  const deliveryDate = form?.deliveryDate ?? request.deliveryDate ?? "";
  const status = form?.status ?? request.status ?? "";

  const assignedName =
    assignedUser?.fullName ||
    assignedUser?.displayName ||
    assignedUser?.name ||
    assignedUser?.email ||
    request.assignedToName ||
    "";

  const createdByDisplay =
    (form?.createdByName && String(form.createdByName).trim()) ||
    (request.createdByName && String(request.createdByName).trim()) ||
    (request.createdBy && String(request.createdBy).trim()) ||
    "Unknown";

  const createdByRole = (form?.createdByRole ?? request.createdByRole ?? "")
    .toString()
    .trim()
    .toLowerCase();

  const itemsFromForm = Array.isArray(form?.items) ? form.items : null;
  const itemsFromRequest = Array.isArray(request?.items) ? request.items : null;

  let items = itemsFromForm && itemsFromForm.length ? itemsFromForm : itemsFromRequest || [];

  if (!items.length && (form?.productId || request.productId || form?.productName || request.productName)) {
    items = [
      {
        productId: form?.productId ?? request.productId ?? null,
        productName: form?.productName ?? request.productName ?? null,
        productCode: form?.productCode ?? request.productCode ?? null,
        quantity: form?.quantity ?? request.quantity ?? 1,
      },
    ].filter((it) => it.productId || it.productName || it.productCode);
  }

  const findProduct = (productId) =>
    inventory.find((inv) => String(inv?.id) === String(productId)) || null;

  const normalizedItems = items
    .filter((it) => it && (it.productId || it.productName || it.productCode))
    .map((it, idx) => {
      const prod = it.productId ? findProduct(it.productId) : null;
      const name =
        prod?.name ||
        (it.productName ? String(it.productName).trim() : "") ||
        (it.name ? String(it.name).trim() : "") ||
        (it.productCode ? String(it.productCode).trim() : "") ||
        `Item ${idx + 1}`;
      const sku = prod?.sku || (it.productCode ? String(it.productCode).trim() : "") || "";
      const qty = Number(it.quantity);
      return {
        name,
        sku,
        quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
      };
    });

  const lines = [];
  lines.push("SAMPLE ORDER DETAIL SHEET");
  lines.push("");
  lines.push("GENERAL");
  lines.push("-------");
  lines.push(`Sample ID : ${request.id || ""}`);
  lines.push(`Status    : ${status || ""}`);
  if (request.orderNumber || form?.orderNumber) {
    lines.push(`Order No. : ${form?.orderNumber || request.orderNumber}`);
  }
  lines.push("");
  lines.push("CREATED BY");
  lines.push("----------");
  lines.push(`Created By   : ${createdByDisplay}`);
  if (createdByRole) lines.push(`Role         : ${createdByRole}`);
  if (createdAt) {
    lines.push(
      `Created On   : ${createdAt.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`
    );
  }
  if (assignedName) lines.push(`Assigned To  : ${assignedName}`);
  lines.push("");
  lines.push("ACCOUNT / CUSTOMER");
  lines.push("------------------");
  lines.push(`Account Name : ${form?.accountName || account?.name || request.accountName || "Not set"}`);

  if (account) {
    const addressLine = account.address?.line1 || account.shippingAddress || account.addressStreet || "";
    const addressLine2 = account.address?.line2 || account.addressStreet2 || "";
    const city = account.address?.city || account.addressCity || account.city || "";
    const state = account.address?.state || account.addressState || account.state || "";
    const zip = account.address?.zip || account.addressZip || account.zip || "";

    if (addressLine) lines.push(`Ship To      : ${addressLine}`);
    if (addressLine2) lines.push(`              ${addressLine2}`);
    if (city || state || zip) {
      lines.push(`City/State/ZIP: ${[city, state, zip].filter(Boolean).join(", ")}`);
    }
    if (account.contact) lines.push(`Contact      : ${account.contact}`);
    if (account.phone) lines.push(`Phone        : ${account.phone}`);
    if (account.email) lines.push(`Email        : ${account.email}`);
  }
  lines.push("");
  lines.push("ITEMS");
  lines.push("-----");
  if (!normalizedItems.length) {
    lines.push("None");
  } else {
    normalizedItems.forEach((item) => {
      lines.push(`- ${item.name}${item.sku ? ` (${item.sku})` : ""} x ${item.quantity}`);
    });
  }
  lines.push("");
  lines.push("DELIVERY");
  lines.push("--------");
  if (requestedDate) lines.push(`Requested Date  : ${requestedDate}`);
  if (deliveryDate) lines.push(`Actual Delivery : ${deliveryDate}`);
  if (form?.deliveryMethod || request.deliveryMethod) {
    lines.push(`Delivery Method : ${form?.deliveryMethod || request.deliveryMethod}`);
  }
  if (form?.trackingNumber || request.trackingNumber) {
    lines.push(`Tracking Number : ${form?.trackingNumber || request.trackingNumber}`);
  }
  if (form?.prepPointOfContactEmail || request.prepPointOfContactEmail) {
    lines.push(`Prep Contact    : ${form?.prepPointOfContactEmail || request.prepPointOfContactEmail}`);
  }
  lines.push("");
  lines.push("NOTES");
  lines.push("-----");
  lines.push(form?.notes ?? request.notes ? String(form?.notes ?? request.notes) : "None");

  const safeAccount = String(request.accountName || "Account").replace(/\s+/g, "_");
  const safeId = String(request.id || "export").replace(/\s+/g, "_");
  const fileName = `SampleOrder_${safeAccount}_${safeId}.pdf`;

  return {
    fileName,
    lines,
    summary: {
      sampleId: request.id || "",
      accountName: form?.accountName || account?.name || request.accountName || "Not set",
      status,
      assignedName,
      requestedDate,
      deliveryDate,
      deliveryMethod: form?.deliveryMethod || request.deliveryMethod || "",
      trackingNumber: form?.trackingNumber || request.trackingNumber || "",
      notes: form?.notes ?? request.notes ?? "",
      items: normalizedItems,
    },
  };
};

export const createSampleOrderPdfDoc = (exportModel) => {
  if (!exportModel) return null;
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
  });

  const marginLeft = 40;
  const marginTop = 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - marginLeft * 2;
  const lineHeight = 14;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Sample Order Detail Sheet", marginLeft, marginTop);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  let y = marginTop + 24;
  const addTextLine = (text) => {
    const wrapped = doc.splitTextToSize(String(text || ""), maxWidth);
    wrapped.forEach((segment) => {
      if (y > pageHeight - marginTop) {
        doc.addPage();
        y = marginTop;
      }
      doc.text(segment, marginLeft, y);
      y += lineHeight;
    });
  };

  exportModel.lines.forEach((line) => addTextLine(line));
  return doc;
};

export const createSampleOrderPdfBase64 = (options) => {
  const exportModel = buildSampleOrderExportModel(options);
  if (!exportModel) return null;
  const doc = createSampleOrderPdfDoc(exportModel);
  const dataUri = doc.output("datauristring");
  const base64 = String(dataUri).split(",")[1] || "";
  return {
    ...exportModel,
    pdfBase64: base64,
  };
};

export const exportSampleDetails = (options) => {
  const exportModel = buildSampleOrderExportModel(options);
  if (!exportModel) return;
  const doc = createSampleOrderPdfDoc(exportModel);

  try {
    doc.save(exportModel.fileName);
  } catch (err) {
    console.error("[exportSampleDetails] Failed to export PDF:", err);
    alert("Could not export sample details as PDF. See console for details.");
  }
};
